import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle, AlertCircle, Check } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { DealReviewDialog } from "@/components/deals/DealReviewDialog";
import { DealsFilter, DealFilters } from "@/components/deals/DealsFilter";

const PendingDeals = () => {
  const [selectedDeal, setSelectedDeal] = useState<any>(null);
  const [filters, setFilters] = useState<DealFilters>({});
  const [selectedDeals, setSelectedDeals] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const { toast } = useToast();

  const { data: deals, isLoading, refetch } = useQuery({
    queryKey: ["pending-deals"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deals" as any)
        .select(`
          *,
          athletes (
            name,
            sport
          )
        `)
        .eq("status", "pending_compliance")
        .order("created_at", { ascending: true });
      
      if (error) throw error;
      return data as any[];
    },
  });

  const handleAction = async (dealId: string, action: "approve" | "reject", reason?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const updates: any = {
        status: action === "approve" ? "active" : "rejected",
        updated_at: new Date().toISOString(),
      };

      if (action === "approve") {
        updates.approved_by = user?.id;
        updates.approved_at = new Date().toISOString();
      } else {
        updates.rejected_by = user?.id;
        updates.rejected_at = new Date().toISOString();
        updates.rejection_reason = reason;
      }

      const { error } = await supabase
        .from("deals" as any)
        .update(updates as any)
        .eq("id", dealId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Deal ${action === "approve" ? "approved" : "rejected"} successfully`,
      });

      setSelectedDeal(null);
      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleBulkAction = async (action: "approve" | "reject", reason?: string) => {
    if (selectedDeals.size === 0) return;

    setIsBulkProcessing(true);
    const { data: { user } } = await supabase.auth.getUser();
    
    let successCount = 0;
    let errorCount = 0;

    for (const dealId of Array.from(selectedDeals)) {
      try {
        const updates: any = {
          status: action === "approve" ? "active" : "rejected",
          updated_at: new Date().toISOString(),
        };

        if (action === "approve") {
          updates.approved_by = user?.id;
          updates.approved_at = new Date().toISOString();
        } else {
          updates.rejected_by = user?.id;
          updates.rejected_at = new Date().toISOString();
          updates.rejection_reason = reason || "Bulk rejection";
        }

        const { error } = await supabase
          .from("deals" as any)
          .update(updates as any)
          .eq("id", dealId);

        if (error) throw error;
        successCount++;
      } catch (error) {
        console.error(`Error processing deal ${dealId}:`, error);
        errorCount++;
      }
    }

    setIsBulkProcessing(false);
    setSelectedDeals(new Set());

    toast({
      title: "Bulk Action Complete",
      description: `${successCount} deals ${action}d successfully${errorCount > 0 ? `, ${errorCount} failed` : ""}`,
    });

    refetch();
  };

  const toggleDealSelection = (dealId: string) => {
    const newSelection = new Set(selectedDeals);
    if (newSelection.has(dealId)) {
      newSelection.delete(dealId);
    } else {
      newSelection.add(dealId);
    }
    setSelectedDeals(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedDeals.size === filteredDeals.length) {
      setSelectedDeals(new Set());
    } else {
      setSelectedDeals(new Set(filteredDeals.map(d => d.id)));
    }
  };

  const filteredDeals = useMemo(() => {
    if (!deals) return [];
    
    return deals.filter((deal) => {
      if (filters.dealType && deal.deal_type !== filters.dealType) return false;
      if (filters.riskLevel && deal.risk_level !== filters.riskLevel) return false;
      if (filters.minAmount && deal.amount < filters.minAmount) return false;
      if (filters.maxAmount && deal.amount > filters.maxAmount) return false;
      if (filters.brandName && !deal.brand_name.toLowerCase().includes(filters.brandName.toLowerCase())) return false;
      if (filters.athleteName && !deal.athletes?.name.toLowerCase().includes(filters.athleteName.toLowerCase())) return false;
      if (filters.startDateFrom && new Date(deal.start_date) < new Date(filters.startDateFrom)) return false;
      if (filters.startDateTo && new Date(deal.start_date) > new Date(filters.startDateTo)) return false;
      
      return true;
    });
  }, [deals, filters]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Pending Deals Queue</h1>
          <p className="text-muted-foreground mt-2">
            Review and approve NIL deals awaiting compliance verification
          </p>
        </div>

        <DealsFilter filters={filters} onFiltersChange={setFilters} showStatusFilter={false} />

        {isLoading ? (
          <div className="flex items-center justify-center h-96">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredDeals.length > 0 ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Checkbox
                  checked={selectedDeals.size === filteredDeals.length}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all deals"
                />
                <p className="text-sm text-muted-foreground">
                  {selectedDeals.size > 0 ? `${selectedDeals.size} selected` : `Showing ${filteredDeals.length} of ${deals?.length || 0} pending deals`}
                </p>
              </div>
              {selectedDeals.size > 0 && (
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleBulkAction("approve")}
                    disabled={isBulkProcessing}
                    size="sm"
                  >
                    {isBulkProcessing ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle className="mr-2 h-4 w-4" />
                    )}
                    Approve Selected ({selectedDeals.size})
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="destructive"
                        disabled={isBulkProcessing}
                        size="sm"
                      >
                        <XCircle className="mr-2 h-4 w-4" />
                        Reject Selected ({selectedDeals.size})
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Bulk Reject Deals</AlertDialogTitle>
                        <AlertDialogDescription>
                          You are about to reject {selectedDeals.size} deals. Please provide a reason:
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <Textarea
                        id="bulk-reject-reason"
                        placeholder="Enter rejection reason for all selected deals..."
                      />
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => {
                            const reason = (document.getElementById("bulk-reject-reason") as HTMLTextAreaElement)?.value;
                            handleBulkAction("reject", reason);
                          }}
                        >
                          Reject All
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
            {filteredDeals.map((deal) => (
              <Card key={deal.id} className={selectedDeals.has(deal.id) ? "border-primary" : ""}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <Checkbox
                      checked={selectedDeals.has(deal.id)}
                      onCheckedChange={() => toggleDealSelection(deal.id)}
                      aria-label={`Select deal ${deal.title}`}
                    />
                    <div className="flex-1">
                      <CardTitle className="text-xl">{deal.title}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        {deal.athletes?.name} - {deal.athletes?.sport}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className="text-lg font-bold">
                        {formatCurrency(deal.amount)}
                      </span>
                      <Badge variant="outline">{deal.deal_type}</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Brand</p>
                        <p className="font-medium">{deal.brand_name}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Start Date</p>
                        <p className="font-medium">
                          {new Date(deal.start_date).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Submitted</p>
                        <p className="font-medium">
                          {new Date(deal.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    {deal.description && (
                      <p className="text-sm text-muted-foreground border-l-2 border-muted pl-4">
                        {deal.description}
                      </p>
                    )}

                    <div className="flex gap-3 pt-2">
                      <Button
                        onClick={() => setSelectedDeal(deal)}
                        variant="outline"
                        className="flex-1"
                      >
                        <AlertCircle className="mr-2 h-4 w-4" />
                        Review Details
                      </Button>
                      <Button
                        onClick={() => handleAction(deal.id, "approve")}
                        variant="default"
                        className="flex-1"
                      >
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => setSelectedDeal(deal)}
                        variant="destructive"
                        className="flex-1"
                      >
                        <XCircle className="mr-2 h-4 w-4" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="text-center py-12">
              <CheckCircle className="h-12 w-12 mx-auto text-green-600 mb-4" />
              <p className="text-muted-foreground">
                No pending deals - All caught up!
              </p>
            </CardContent>
          </Card>
        )}

        {selectedDeal && (
          <DealReviewDialog
            deal={selectedDeal}
            open={!!selectedDeal}
            onOpenChange={(open) => !open && setSelectedDeal(null)}
            onAction={handleAction}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default PendingDeals;